/**
 * Kitchen Estimator — Configuration & Default Rates
 * All rates in INR. Sheet size = 8x4 ft (32 sqft).
 */

export const KITCHEN_LAYOUTS = [
  { id: 'straight', label: 'Straight Kitchen', walls: 1, icon: '━' },
  { id: 'l_shape', label: 'L-Shape', walls: 2, icon: '┗' },
  { id: 'u_shape', label: 'U-Shape', walls: 3, icon: '┗┛' },
  { id: 'parallel', label: 'Parallel (Galley)', walls: 2, icon: '║' },
  { id: 'island', label: 'Island', walls: 2, icon: '◼' },
  { id: 'peninsula', label: 'Peninsula', walls: 2, icon: '┻' },
];

// ===== MODULE TYPES =====
export const BASE_UNITS = [
  { id: 'sink', label: 'Sink Unit', defaultWidthMm: 900, defaultHeightMm: 850, defaultDepthMm: 580, defaultDrawers: 0, hasShutter: true },
  { id: 'drawer', label: 'Drawer Unit', defaultWidthMm: 600, defaultHeightMm: 850, defaultDepthMm: 580, defaultDrawers: 3, hasShutter: true },
  { id: 'hob', label: 'Hob Unit', defaultWidthMm: 900, defaultHeightMm: 850, defaultDepthMm: 580, defaultDrawers: 2, hasShutter: true },
  { id: 'standard_shutter_base', label: 'Standard Shutter Base', defaultWidthMm: 600, defaultHeightMm: 850, defaultDepthMm: 580, defaultDrawers: 0, hasShutter: true },
  { id: 'corner', label: 'Corner Unit', defaultWidthMm: 900, defaultHeightMm: 850, defaultDepthMm: 900, defaultDrawers: 0, hasShutter: true, isCorner: true },
  { id: 'magic_corner', label: 'Magic Corner Unit', defaultWidthMm: 900, defaultHeightMm: 850, defaultDepthMm: 900, defaultDrawers: 0, hasShutter: true, isCorner: true, hasTrolley: 'magic_corner' },
  { id: 'bottle_pullout', label: 'Bottle Pullout', defaultWidthMm: 300, defaultHeightMm: 850, defaultDepthMm: 580, defaultDrawers: 0, hasShutter: true, hasTrolley: 'bottle' },
  { id: 'dustbin', label: 'Dustbin Unit', defaultWidthMm: 450, defaultHeightMm: 850, defaultDepthMm: 580, defaultDrawers: 0, hasShutter: true },
  { id: 'dishwasher', label: 'Dishwasher Unit', defaultWidthMm: 600, defaultHeightMm: 850, defaultDepthMm: 580, defaultDrawers: 0, hasShutter: true },
  { id: 'utility_base', label: 'Utility Base', defaultWidthMm: 600, defaultHeightMm: 850, defaultDepthMm: 580, defaultDrawers: 0, hasShutter: true },
  { id: 'tandem_stack', label: 'Tandem Drawer Stack', defaultWidthMm: 600, defaultHeightMm: 850, defaultDepthMm: 580, defaultDrawers: 4, hasShutter: true, hasTrolley: 'tandem' },
  { id: 'tall_pantry_base', label: 'Tall Pantry Base', defaultWidthMm: 450, defaultHeightMm: 850, defaultDepthMm: 580, defaultDrawers: 0, hasShutter: true },
];

export const WALL_UNITS = [
  { id: 'hinged', label: 'Standard Wall Cabinet', defaultWidthMm: 600, defaultHeightMm: 700, defaultDepthMm: 350, hasShutter: true },
  { id: 'lift_up', label: 'Lift-Up Cabinet', defaultWidthMm: 600, defaultHeightMm: 700, defaultDepthMm: 350, hasShutter: true, liftUp: true },
  { id: 'glass_display', label: 'Glass Display Unit', defaultWidthMm: 600, defaultHeightMm: 700, defaultDepthMm: 350, hasShutter: true },
  { id: 'open_shelf', label: 'Open Shelf', defaultWidthMm: 600, defaultHeightMm: 700, defaultDepthMm: 300, hasShutter: false },
  { id: 'chimney_box', label: 'Chimney Box', defaultWidthMm: 900, defaultHeightMm: 600, defaultDepthMm: 350, hasShutter: true, isChimney: true },
  { id: 'microwave', label: 'Microwave Unit', defaultWidthMm: 600, defaultHeightMm: 450, defaultDepthMm: 350, hasShutter: true },
  { id: 'wall_corner', label: 'Corner Wall Unit', defaultWidthMm: 600, defaultHeightMm: 700, defaultDepthMm: 600, hasShutter: true, isCorner: true },
  { id: 'plate_rack', label: 'Plate Rack', defaultWidthMm: 600, defaultHeightMm: 700, defaultDepthMm: 350, hasShutter: true },
];

export const TALL_UNITS = [
  { id: 'pantry', label: 'Pantry Unit', defaultWidthMm: 600, defaultHeightMm: 2400, defaultDepthMm: 580, hasShutter: true, hasTrolley: 'pantry' },
  { id: 'fridge_housing', label: 'Refrigerator Housing', defaultWidthMm: 750, defaultHeightMm: 2400, defaultDepthMm: 650, hasShutter: false },
  { id: 'oven_tower', label: 'Oven Tower', defaultWidthMm: 600, defaultHeightMm: 2400, defaultDepthMm: 580, hasShutter: true },
  { id: 'utility_broom', label: 'Utility / Broom Unit', defaultWidthMm: 450, defaultHeightMm: 2400, defaultDepthMm: 580, hasShutter: true },
  { id: 'utility_tall', label: 'Utility Tall Unit', defaultWidthMm: 600, defaultHeightMm: 2400, defaultDepthMm: 580, hasShutter: true },
  { id: 'crockery_tall', label: 'Crockery Tall Unit', defaultWidthMm: 600, defaultHeightMm: 2400, defaultDepthMm: 580, hasShutter: true },
];

export const MODULE_CATEGORIES = { base: BASE_UNITS, wall: WALL_UNITS, tall: TALL_UNITS };

// ===== MATERIALS & RATES =====
export const CARCASS_MATERIALS = [
  { id: 'bwp', label: 'BWP Plywood', rate: 95 },     // Rs/sqft
  { id: 'bwr', label: 'BWR Plywood', rate: 75 },
  { id: 'hdhmr', label: 'HDHMR', rate: 85 },
  { id: 'marine', label: 'Marine Ply', rate: 120 },
  { id: 'mdf', label: 'MDF (Budget)', rate: 45 },
  { id: 'ss', label: 'Stainless Steel', rate: 250 },
];

export const SHUTTER_MATERIALS = [
  { id: 'laminate', label: 'Laminate', rate: 140 },
  { id: 'acrylic', label: 'Acrylic', rate: 380 },
  { id: 'pu', label: 'PU Painted', rate: 480 },
  { id: 'veneer', label: 'Veneer', rate: 420 },
  { id: 'glass', label: 'Glass Shutter', rate: 320 },
  { id: 'membrane', label: 'Membrane', rate: 200 },
  { id: 'aluminum', label: 'Aluminum Frame + Glass', rate: 280 },
];

// ===== DRAWER / TROLLEY SYSTEMS (Rs per unit) =====
export const DRAWER_SYSTEMS = [
  { id: 'none', label: 'None', rate: 0 },
  { id: 'telescopic', label: 'Telescopic Channel', rate: 450 },
  { id: 'telescopic_sc', label: 'Telescopic (Soft-Close)', rate: 750 },
  { id: 'tandem', label: 'Tandem Drawer', rate: 2800 },
  { id: 'tandem_premium', label: 'Tandem (Premium Blum)', rate: 4500 },
];

export const TROLLEY_SYSTEMS = [
  { id: 'ss304_thali', label: 'SS 304 Thali Basket', rate: 2200 },
  { id: 'ss304_cupsaucer', label: 'Cup & Saucer Basket', rate: 1800 },
  { id: 'bottle', label: 'Bottle Pullout', rate: 2400 },
  { id: 'pantry', label: 'Pantry Pullout (Tall)', rate: 8500 },
  { id: 'corner_carousel', label: 'Corner Carousel', rate: 5800 },
  { id: 'magic_corner', label: 'Magic Corner', rate: 9200 },
  { id: 'cutlery_tray', label: 'Cutlery Tray (600mm)', rate: 850 },
];

// ===== COUNTERTOPS (Rs/sqft) =====
export const COUNTERTOP_MATERIALS = [
  { id: 'granite', label: 'Granite', rate: 220 },
  { id: 'quartz', label: 'Quartz', rate: 480 },
  { id: 'solid_surface', label: 'Solid Surface', rate: 620 },
  { id: 'sintered', label: 'Sintered Stone', rate: 780 },
  { id: 'marble', label: 'Marble', rate: 380 },
  { id: 'custom', label: 'Custom (specify name & rate)', rate: 0, isCustom: true },
];

export const EDGE_PROFILES = [
  { id: 'plain', label: 'Plain', rate: 0 },
  { id: 'bullnose', label: 'Bullnose (Half Round)', rate: 120 }, // Rs/running ft
  { id: 'bevel', label: 'Bevel', rate: 80 },
  { id: 'mitred', label: 'Mitred (90°)', rate: 200 },
  { id: 'dupont', label: 'Dupont / Ogee', rate: 180 },
];

// ===== BACKSPLASH (Rs/sqft) =====
export const BACKSPLASH_MATERIALS = [
  { id: 'none', label: 'None', rate: 0 },
  { id: 'tile', label: 'Ceramic / Porcelain Tile', rate: 90 },
  { id: 'quartz', label: 'Quartz Slab', rate: 450 },
  { id: 'glass', label: 'Back-Painted Glass', rate: 380 },
  { id: 'laminate', label: 'Laminate Back Panel', rate: 180 },
  { id: 'stone', label: 'Stone Cladding', rate: 260 },
];

// ===== APPLIANCES (indicative retail prices in INR, client can override) =====
export const APPLIANCES = [
  { id: 'hob', label: 'Hob (4-burner)', defaultPrice: 18000 },
  { id: 'chimney', label: 'Chimney (90cm)', defaultPrice: 22000 },
  { id: 'oven', label: 'Built-in Oven', defaultPrice: 42000 },
  { id: 'microwave', label: 'Microwave', defaultPrice: 16000 },
  { id: 'dishwasher', label: 'Dishwasher', defaultPrice: 55000 },
  { id: 'refrigerator', label: 'Refrigerator (Double Door)', defaultPrice: 48000 },
  { id: 'water_purifier', label: 'RO Water Purifier', defaultPrice: 14000 },
];

// ===== SINK / PLUMBING =====
export const SINK_TYPES = [
  { id: 'single_ss', label: 'Single Bowl SS', rate: 4500 },
  { id: 'double_ss', label: 'Double Bowl SS', rate: 8500 },
  { id: 'granite_single', label: 'Granite Composite (Single)', rate: 12000 },
  { id: 'granite_double', label: 'Granite Composite (Double)', rate: 18000 },
  { id: 'ss_undermount', label: 'SS 304 Undermount Premium', rate: 15000 },
];

export const FAUCET_TYPES = [
  { id: 'basic', label: 'Basic Swivel Mixer', rate: 2500 },
  { id: 'pullout', label: 'Pull-out Spray Mixer', rate: 6500 },
  { id: 'sensor', label: 'Sensor Faucet', rate: 14000 },
];

// ===== PLUMBING ENGINE =====
// Scope-based lump-sum base rate (admin editable)
export const PLUMBING_SCOPES = [
  { id: 'none', label: 'None', baseRate: 0 },
  { id: 'standard', label: 'Standard Installation (no shifting)', baseRate: 2500 },
  { id: 'minor', label: 'Minor Modification', baseRate: 5000 },
  { id: 'major', label: 'Major Rework / Full Re-route', baseRate: 12000 },
  { id: 'custom', label: 'Custom (manual entry)', baseRate: 0 },
];

// Add-on fixture/item rates (Rs per unit of labour — material extra if enabled)
export const PLUMBING_ADDONS = [
  { id: 'sink_install', label: 'Sink Installation', rate: 1200, autoFor: ['sink'] },
  { id: 'faucet_install', label: 'Faucet Installation', rate: 800, autoFor: ['sink'] },
  { id: 'ro_point', label: 'RO Point', rate: 1500, autoFor: ['ro'] },
  { id: 'dishwasher_point', label: 'Dishwasher Point', rate: 2500, autoFor: ['dishwasher'] },
  { id: 'washing_point', label: 'Washing Machine Point', rate: 2000, autoFor: ['utility_base', 'utility_tall'] },
  { id: 'extra_tap', label: 'Extra Tap', rate: 700 },
  { id: 'drain_relocation', label: 'Drain Relocation', rate: 3500 },
  { id: 'leak_testing', label: 'Leak Testing / Commissioning', rate: 900 },
];

// Optional plumbing materials (for "Labour + Material" billing mode)
export const PLUMBING_MATERIALS_FLAT = 3500; // bundled CPVC/UPVC/valves/connectors/hoses/traps/sealant — single editable flat line

export const PLUMBING_BY_SCOPE = Object.fromEntries(PLUMBING_SCOPES.map(s => [s.id, s]));
export const PLUMBING_ADDON_BY_ID = Object.fromEntries(PLUMBING_ADDONS.map(a => [a.id, a]));

// ===== ELECTRICAL (Rs per unit or linear — admin amendable)
export const ELECTRICAL_ITEMS = [
  { id: 'socket', label: 'Extra Socket Point', rate: 900, unit: 'each' },
  { id: 'under_cabinet_led', label: 'Under-Cabinet LED Profile', rate: 760, unit: 'per ft' },
  { id: 'strip_light', label: 'Shelf Strip Light', rate: 560, unit: 'per ft' },
  { id: 'driver', label: 'LED Driver (60W)', rate: 2400, unit: 'each' },
  { id: 'sensor', label: 'Motion Sensor Switch', rate: 1700, unit: 'each' },
  { id: 'appliance_point', label: 'Appliance Point (16A)', rate: 1360, unit: 'each' },
];

// ===== HARDWARE PRESETS (Rs per unit) =====
export const HARDWARE_DEFAULTS = {
  hingeRate: 120,
  softCloseHingeRate: 180,
  handleRate: 250,        // per handle
  liftUpStayRate: 950,
  legRate: 60,
  skirtingRate: 40,       // per running ft
  connectorRate: 12,
  shelfSupportRate: 8,
};

// ===== HELPERS =====
export const CARCASS_BY_ID = Object.fromEntries(CARCASS_MATERIALS.map(m => [m.id, m]));
export const SHUTTER_BY_ID = Object.fromEntries(SHUTTER_MATERIALS.map(m => [m.id, m]));
export const DRAWER_BY_ID = Object.fromEntries(DRAWER_SYSTEMS.map(m => [m.id, m]));
export const TROLLEY_BY_ID = Object.fromEntries(TROLLEY_SYSTEMS.map(m => [m.id, m]));
export const COUNTERTOP_BY_ID = Object.fromEntries(COUNTERTOP_MATERIALS.map(m => [m.id, m]));
export const BACKSPLASH_BY_ID = Object.fromEntries(BACKSPLASH_MATERIALS.map(m => [m.id, m]));
export const EDGE_BY_ID = Object.fromEntries(EDGE_PROFILES.map(m => [m.id, m]));
export const APPLIANCE_BY_ID = Object.fromEntries(APPLIANCES.map(m => [m.id, m]));
export const SINK_BY_ID = Object.fromEntries(SINK_TYPES.map(m => [m.id, m]));
export const FAUCET_BY_ID = Object.fromEntries(FAUCET_TYPES.map(m => [m.id, m]));

// Returns the module def (from any category)
export function getModuleDef(category, typeId) {
  return (MODULE_CATEGORIES[category] || []).find(m => m.id === typeId);
}

// Hinge count from shutter height (mm) — matches WardrobeCalculator rule
export function hingesForHeight(heightMm) {
  const inches = heightMm / 25.4;
  if (inches <= 36) return 2;
  if (inches <= 60) return 3;
  if (inches <= 84) return 4;
  return 5;
}
